import React, { useEffect, useRef, useState, useTransition } from 'react'
import Header from '../components/Header';
import { SaveFab } from '../components/Buttons';
import { TabBar, TabPanel } from '../components/Tabs';
import { Button, Grid, Tab } from '@mui/material';
import Content from '../components/Content';
import { Field, Form, Formik, FormikValues } from 'formik';
import { TextField } from 'formik-mui';
import { DatePicker } from 'formik-mui-x-date-pickers';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { axios } from '../api';
import { useSnackbar } from 'notistack';
import { AxiosResponse, Method } from 'axios';
import {  InspectionDetailView } from './types';
import { LoadingView } from '../components/Loading';
import CustomSnackbarContent from '../components/CustomSnackbarContent';
import { handleAPIErrors } from '../utils/FormikErrors';
import * as Yup from "yup";
import InspectionComponent from './InspectionComponent';
import CreepTypeField from '../components/CreepTypeField';
import AtaChapterAutocomplete from '../ata-chapters/AtaChapterAutocomplete';
import AlertDialog from '../components/AlertDialog';
import {
    RECORD_LIST_TAB_BODY,
    useInspectionRecordTabs,
} from '../inspections-tr/useInspectionRecordTabs';
import { useAircraftReturnUrl } from '../utils/recordReturn';

interface InspectionDetailProps {
    originalUrl: any
}


function a11yProps(index: any) {
    return {
        id: `simple-tab-${index}`,
        "aria-controls": `simple-tabpanel-${index}`,
    };
}

const InspectionDetail: React.FC<InspectionDetailProps> = (props) => {
    const { originalUrl } = props
    const {id} = useParams()
    const navigate = useNavigate();
    const {enqueueSnackbar} = useSnackbar()
    const [title, setTitle] = useState("")
    // The active tab is held by KEY: the gated tab set arrives after first
    // paint (2 tabs, then up to 8), so an index would silently re-point a
    // click made before it landed at a different tab.
    //
    // `?tab=<key>` opens on that tab ("Open task / check list" links "?tab=task_list"; until
    // 2026-10-12 the record ignored it and opened on General).
    const [search] = useSearchParams()
    const [tabKey, setTabKey] = useState<string>(() => search.get("tab") || "general")
    // Opened from an aircraft's Inspections tab: Close / Save & Close go back to it (BP-97).
    const returnUrl = useAircraftReturnUrl()
    const formRef = useRef<FormikValues>()
    const [inspection,setInspection] = useState<InspectionDetailView>()
    let isRendered = useRef(false);
    const [tabDialogOpen, setTabDialogOpen] = useState(false);
    // The tab the user clicked while General was dirty — the dialog's Save
    // goes THERE, not to a fixed index (the tab set is gated per record).
    const [pendingTab, setPendingTab] = useState<string | null>(null);
    const [, startTransition] = useTransition();
    // H98 — legacy's inspection-record tabs, in legacy's order, gated by the
    // backend (creep_type 6 hides BOM + Linked To; Task/Check List needs a
    // template DB). See src/inspections-tr/useInspectionRecordTabs.tsx.
    const recordTabs = useInspectionRecordTabs(id ? Number(id) : undefined);
    const tabValue = Math.max(0, recordTabs.findIndex((t) => t.key === tabKey));
    useEffect(() => {
        isRendered.current = true;
        if (id) {
          axios
            .get(`/v6/inspection/${id}`, { id: `get-inspection-${id}` })
            .then((resp: AxiosResponse<InspectionDetailView>) => {
              if (isRendered.current) {
                const inspection = {};
                for (const value in resp.data) {
                    inspection[value] = resp.data[value] === null ? "" : resp.data[value];
                }
               
                setInspection(inspection as InspectionDetailView);
              }
    
              return null;
            })
            .catch((error) => {
              enqueueSnackbar(`Error fetching Inspection`, {
                variant: "error",
                anchorOrigin: {
                  vertical: "bottom",
                  horizontal: "center",
                },
              });
              console.error(error);
            });
        }
        else{
            const new_inspection:InspectionDetailView = {
                title: "",
                reference: "",
                version_date: "",
                version_url: "",
                creep_type: 0,
                tolerance: 0,
                version_no: "",
                created: "",
                notes: "",
                inspection_type_id:0
              }
            startTransition(()=>{
                setInspection(new_inspection as InspectionDetailView)
                setTitle("New Inspection")
            })
        }
    },[id,enqueueSnackbar])

    // H98 (Aaron 2026-09-28: "inspection title needs to be in the title") —
    // the record header names the inspection, as Blank's edit header does
    // (src/inspections-tr/InspectionsTREdit.tsx), falling back to the id only
    // when the record has no title.
    useEffect(() => {
        if (id) {
          if (inspection && "id" in inspection) {
            setTitle(inspection.title || `Inspection #${inspection.id}`);
          }
        }
      }, [id, inspection]);


    const submitForm = () => {
      if (formRef.current) {
        formRef.current.submitForm().then(() => {
          if (
            formRef.current &&
            !formRef.current.isValidating &&
            formRef.current.isValid
          ) {
            navigate(returnUrl ?? "..");
          }
        });
      }
    else{
        navigate(returnUrl ?? "..")
    }
    };
    
    const saveForm = () => {
      if (formRef.current) {
        formRef.current.submitForm()
      }

    };
    
    const handleClose = () => {
        if (formRef.current) {
            formRef.current.submitForm().then(() => {
                if (
                  formRef.current &&
                  !formRef.current.isValidating &&
                  formRef.current.isValid
                ) {

                  setTabKey(pendingTab ?? "aircraft_list");
                  setPendingTab(null);
                }
              });
        }
      };
    
    const handleTabChange = (
        _event: React.SyntheticEvent<{}> | null,
        newValue: unknown) => {
            // setTabValue(newValue as number)
          
            if (
                formRef.current &&
                ((formRef.current.dirty && !formRef.current.isSubmitting) ||
                  !formRef.current.initialValues.id)
              ) {
                
                setPendingTab(recordTabs[newValue as number]?.key ?? null);
                setTabDialogOpen(true);
               
              }
              else{
                setTabKey(recordTabs[newValue as number]?.key ?? "general");
              }
              return;
    }
    return (
        <>
            <Header moduleName="Inspections" closeUrl={returnUrl ?? ".."} actions={[<SaveFab onClick2={saveForm} onClick={submitForm} key={1} />]}>
                {title}
            </Header>
            <TabBar value={tabValue} onChange={handleTabChange}>
                {recordTabs.map((t, i) => (
                    <Tab key={t.key} disableRipple label={t.label} {...a11yProps(i)} />
                ))}
            </TabBar>
            <Content>
                {recordTabs.map((t, i) => (
                    <TabPanel
                        key={t.key}
                        value={tabValue}
                        index={i}
                        {...(t.key === "general" ? { "data-testid": "inspection-form" } : {})}
                    >
                        {!inspection ? (
                            <LoadingView />
                        ) : t.key === "general" ? (
                            <General formRef={formRef} originalUrl={originalUrl} inspection={inspection} setInspection={setInspection} />
                        ) : t.key === "aircraft_list" ? (
                            <InspectionComponent formRef={formRef} originalUrl={originalUrl} inspection={inspection} />
                        ) : inspection.id ? (
                            RECORD_LIST_TAB_BODY[t.key]?.(Number(inspection.id))
                        ) : null}
                    </TabPanel>
                ))}
            </Content>
            <AlertDialog
            title="Are you sure ?"
            description="Inspection is not saved"
            open={tabDialogOpen}
            handleClose={() => {
              setTabDialogOpen(false);
            }}
          >
            <Button
              onClick={() => {
                setTabDialogOpen(false);
              }}
              color="primary"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (formRef.current) {
                  formRef.current.resetForm();
                  setTabDialogOpen(false);
                }
              }}
              color="primary"
            >
              Reset
            </Button>
            <Button
              onClick={() => {
                setTabDialogOpen(false);
                handleClose();
              }}
              color="primary"
              autoFocus
            >
              Save
            </Button>
          </AlertDialog>
        </>
    )
}


export interface GeneralTabProps {
    originalUrl: string;
    formRef?: any,
    inspection:InspectionDetailView
    setInspection:any;
}

const General = (props: GeneralTabProps) => {
    const { formRef ,inspection,setInspection} = props
  
    const { enqueueSnackbar } = useSnackbar();
    const location = useLocation();


    const inspectionSchema = Yup.object().shape({
        title: Yup.string().required("Required"),
      });

    return (
        <>
            <Formik
                enableReinitialize
                initialValues={inspection}
                innerRef={formRef}
                validationSchema={inspectionSchema}
                onSubmit={(values, { setSubmitting, setErrors, resetForm }) => {
                    let method: Method = "post";
                    let url:string = `/v6/inspection`;
                    let msg: string = "created";

                    if (Object.prototype.hasOwnProperty.call(inspection, "id") && inspection.id) {
                        method = "put";
                        url = `/v6/inspection/${inspection.id}`;
                        msg = "updated";
                    }

                    const inspection_data = Object.entries(values).reduce((acc,[key,value])=>{
                        acc[key] = value

                        return acc;
                    },{} as InspectionDetailView)

                    axios({
                        url: url,
                        method: method,
                        data: inspection_data
                    }).then((resp: AxiosResponse<InspectionDetailView>)=>{
                        axios.storage.remove(`get-inspection-${inspection.id}`)
                        setInspection(resp.data)
                        let uri = location.pathname;
                        uri = uri.replace("create",`${resp.data.id}`)
                        enqueueSnackbar(
                            <CustomSnackbarContent
                              message={`Successfully ${msg} Inspection`}
                              uri={uri}
                            />,
                            {
                              variant: "success",
                            }
                          );
                        resetForm();
                        // navigate(-1)
                    }).catch((error)=>{
                        console.debug(error,"error")
                        handleAPIErrors(error, setErrors);
                        setSubmitting(false);
                        // A 422 names the field (detail[0].loc[1]); a 409 -- e.g. the ATA chapter
                        // on a tenant without version-25 -- is a plain string. Reading the first
                        // shape off the second threw here, and the user saw nothing.
                        const detail = error?.response?.data?.detail;
                        const why = typeof detail === "string" ? detail : detail?.[0]?.loc?.[1] ?? "";
                        enqueueSnackbar(`Error saving Inspection ${why}`, {
                            variant: "error",
                        });
                    })
                }}
            >
                {({ isSubmitting, setFieldValue, values, setValues }) => (
                    <Form className='mui-form'>
                        <Grid container spacing={2}>

                            <Grid container spacing={2}>
                                <Grid item className="section-padding" xs={12} md={4} lg={4}>
                                    <Field
                                        autoFocus
                                        required
                                        fullWidth
                                        disabled
                                        component={TextField}
                                        label="Inspection Type"
                                        name="inspection_type_id"
                                        // Display-only: this input shows the resolved type
                                        // NAME (type_obj.name) while the scalar `name` prop
                                        // keeps Formik's own numeric inspection_type_id state
                                        // wired for submit. Passing `value` straight to
                                        // <Field> here used to fight Formik's own controlled
                                        // value for the same name -- undefined whenever
                                        // type_obj hadn't resolved yet, which is exactly the
                                        // "uncontrolled -> controlled" input flip React warns
                                        // about. Route it through the TextField's inputProps
                                        // instead so it never touches Formik's value channel.
                                        inputProps={{ value: inspection?.type_obj?.name ?? "" }}
                                    />
                                    <Field
                                        autoFocus
                                        required
                                        fullWidth
                                        component={TextField}
                                        label="Version No."
                                        name="version_no"

                                    />
                                    <Field
                                        autoFocus
                                        required
                                        fullWidth
                                        component={TextField}
                                        label="Tolerance"
                                        name="tolerance"

                                    />
                                </Grid>

                                <Grid item className="section-padding" xs={12} md={4} lg={4}>
                                    <Field
                                        autoFocus
                                        required
                                        fullWidth
                                        component={TextField}
                                        label="Subject"
                                        name="title"

                                    />
                                    <Field
                                        autoFocus
                                        required
                                        fullWidth
                                        component={TextField}
                                        label="Url"
                                        name="version_url"

                                    />
                                    {/* A labelled select, not a raw number (Aaron 2026-10-07):
                                        the default each limitation inherits. */}
                                    <CreepTypeField required />
                                </Grid>

                                <Grid item className="section-padding" xs={12} md={4} lg={4}>
                                    <Field
                                        autoFocus
                                        required
                                        fullWidth
                                        component={TextField}
                                        label="Reference Number"
                                        name="reference"

                                    />
                                    {/* The inspection's ATA chapter (Aaron 2026-10-09). Only a pick
                                        posts ata_chapter_id; an untouched form leaves it alone. */}
                                    <AtaChapterAutocomplete
                                        value={values.ata_chapter ?? null}
                                        onChange={(chapter) => {
                                            setFieldValue("ata_chapter", chapter);
                                            setFieldValue("ata_chapter_id", chapter?.id ?? null);
                                        }}
                                    />
                                    <Field
                                        autoFocus
                                        required
                                        fullWidth
                                        component={DatePicker}
                                        label="Date Created"
                                        name="created"

                                    />
                                </Grid>

                            </Grid>
                           <Grid item className="section-padding"  xs={12} md={12} lg = {12} ></Grid>
                            <Grid container spacing={2}>
                                <Grid item className="section-padding" xs={12} md={12} lg = {12} >
                                    <Field
                                    autoFocus
                                    required
                                    fullWidth
                                    multiline
                                    rows="5"
                                    component={TextField}
                                    label="Instruction"
                                    name="notes"
                                    />
                                </Grid>
                            </Grid>

                        </Grid>
                    </Form>
                )
                }

            </Formik>
        </>
    )
}

export default InspectionDetail
